import { useEffect, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { authFetch } from "../../utils/authFetch";
import { setImpersonationToken } from "../../utils/resolveAuthToken";

interface Target {
  /** null for a pending invite — no user row exists for them yet. */
  id: string | null;
  inviteId?: string;
  email: string;
  name: string | null;
  userType: "internal" | "external";
  pendingInvite?: boolean;
  inviteLive?: boolean;
}

/** A near-miss: matched the search but cannot be a target. Shown with the
 * API's own reason rather than dropped — a blank list reads as "impersonation
 * is broken" when the truth is "that person has never signed in". */
interface Unavailable {
  email: string;
  reason: string;
}

/** Inline target search, rendered only for a super admin.
 *
 * Because /api/me returns the TARGET's profile during a session, isSuperAdmin
 * reads false once one starts and this control disappears on its own — so the
 * operator cannot reach for a nested start. That is a convenience, not the
 * control: nesting is refused server-side regardless. */
export default function ImpersonatePicker() {
  const { user } = useAuth();
  const [q, setQ] = useState("");
  const [targets, setTargets] = useState<Target[]>([]);
  const [unavailable, setUnavailable] = useState<Unavailable[]>([]);
  const [confirming, setConfirming] = useState<Target | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (q.length < 2) {
      setTargets([]);
      setUnavailable([]);
      return;
    }
    const controller = new AbortController();
    const t = setTimeout(async () => {
      try {
        const res = await authFetch(
          `/api/admin/impersonation/targets?q=${encodeURIComponent(q)}`,
          { signal: controller.signal },
        );
        if (res.ok) {
          const body = await res.json();
          setTargets(body.targets);
          setUnavailable(body.unavailable ?? []);
        }
      } catch {
        /* aborted or offline */
      }
    }, 300);
    return () => {
      clearTimeout(t);
      controller.abort();
    };
  }, [q]);

  if (!user?.isSuperAdmin) return null;

  const start = async (target: Target) => {
    setError(null);
    const res = await authFetch("/api/admin/impersonation", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(
        target.inviteId ? { invite_id: target.inviteId } : { target_user_id: target.id },
      ),
    });
    if (!res.ok) {
      // Surface the route's own words — it is the single refusal predicate.
      const body = await res.json().catch(() => ({}));
      setError(body.detail ?? "Could not start");
      return;
    }
    setImpersonationToken((await res.json()).token);
    // Land where this identity belongs, rather than reloading the operator's
    // own url: the picker only ever renders on a /studio page (HeaderRow1),
    // which an external target cannot open — AppGuard bounces them and the
    // chain ends on a blank docs page. "/" lets HomeRedirect choose from the
    // TARGET's entitlements, and ?surface=docs is the partner surface the
    // design calls for (spec §8 — detectSurface returns the same config the
    // real docs.joveo.ai host does).
    window.location.assign(target.userType === "external" ? "/?surface=docs" : "/");
  };

  if (confirming) {
    return (
      <div className="px-3 py-2 border-t border-slate-100 text-[11px] text-slate-600">
        <p className="mb-2">
          View as <strong className="text-slate-800">{confirming.email}</strong> for 30 minutes.
          Everything you do is performed as them and recorded against your own account.
          They are not notified.
        </p>
        {confirming.pendingInvite && (
          <p className="mb-2">
            They have never signed in, so this <strong className="text-slate-800">accepts
            their invite now</strong> — it creates their account and applies every grant
            their pending invites carry, exactly as their own first sign-in would. Their
            emailed invite link then reports as already used and cannot be resent, so they
            may need a nudge to sign in; their name is filled in when they do.
          </p>
        )}
        {/* The panel needs its own error slot: the search-box one below is in
            the other branch, so a refusal from start() used to leave Start
            looking like it simply did nothing — and the invite path makes
            three of its four refusals reachable on a first click. */}
        {error && <p className="mb-2 text-[11px] text-red-600">{error}</p>}
        <button type="button" onClick={() => start(confirming)}
          className="mr-3 font-medium text-amber-700 underline hover:no-underline">
          Start
        </button>
        <button type="button" onClick={() => setConfirming(null)}
          className="text-slate-500 underline hover:no-underline">
          Cancel
        </button>
      </div>
    );
  }

  return (
    <div className="px-3 py-2 border-t border-slate-100">
      <input
        type="search"
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Impersonate user…"
        className="w-full rounded border border-slate-200 px-2 py-1 text-[12px] text-slate-700 placeholder:text-slate-400"
      />
      {error && <p className="mt-1 text-[11px] text-red-600">{error}</p>}
      <ul className="mt-1 max-h-48 overflow-auto">
        {targets.map((t) => (
          <li key={t.id ?? t.inviteId}>
            <button
              type="button"
              onClick={() => setConfirming(t)}
              className="w-full truncate rounded px-1.5 py-1 text-left text-[12px] text-slate-700 hover:bg-slate-50"
            >
              {t.email}
              {t.pendingInvite ? (
                <span className="ml-1.5 text-[11px] text-slate-400">
                  {t.inviteLive === false ? "invite lapsed" : "invited"}
                </span>
              ) : t.userType === "external" && (
                <span className="ml-1.5 text-[11px] text-slate-400">partner</span>
              )}
            </button>
          </li>
        ))}
        {unavailable.map((u) => (
          <li key={u.email} className="truncate px-1.5 py-1 text-[12px] text-slate-400">
            {u.email}
            <span className="ml-1.5 text-[11px] italic">{u.reason}</span>
          </li>
        ))}
        {q.length >= 2 && targets.length === 0 && unavailable.length === 0 && (
          <li className="px-1.5 py-1 text-[11px] text-slate-400">
            No match. People who have not signed in yet are found by email address only.
          </li>
        )}
      </ul>
    </div>
  );
}
